import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Clock, TrendingUp, Flame, Heart, NotebookPen,
  Edit3, Save, X, Cloud, AlertCircle, Activity as ActivityIcon, Footprints, Bike, Mountain,
  ExternalLink, Trash2,
} from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { recoveryActivities } from '../data/endurance';
import {
  distanceUnit, elevationUnit, displayDistance, displayElevation,
  parseDistanceInput, mToFt,
} from '../utils/units';

// Distance-based activities get the "GPS-style" detail layout. Others get the simpler session-log layout.
const DISTANCE_ACTIVITIES = new Set(['walk-run', 'cycling', 'hiking', 'rowing', 'swimming']);

const Stat = ({ icon: Icon, label, value, unit }) => (
  <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-4">
    <Icon className="w-4 h-4 text-enf-accent-fg mb-2" />
    <div className="font-display text-3xl tracking-wide leading-none">
      {value || <span className="text-enf-text-muted">—</span>}
      {value && unit && <span className="text-xs text-enf-text-muted ml-1">{unit}</span>}
    </div>
    <div className="text-xs text-enf-text-muted uppercase tracking-wider mt-2">{label}</div>
  </div>
);

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, history, update, removeWorkout } = useProfile();
  const units = profile.units || 'imperial';
  const session = useMemo(() => history.find((h) => h.id === id), [history, id]);

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Edit-draft values are in USER UNITS so the form fields match what they see.
  // Conversion to canonical happens at save time.
  const [draft, setDraft] = useState({
    duration: session?.duration ?? '',
    distance: session?.distance != null ? displayDistance(session.distance, units) : '',
    notes: session?.meta?.notes ?? '',
    heartRateAvg: session?.meta?.heartRateAvg ?? '',
    calories: session?.meta?.calories ?? '',
    elevation: session?.meta?.elevation != null ? displayElevation(session.meta.elevation, units) : '',
    // Mode + Surface — editable for any activity that has modes
    mode: session?.meta?.mode ?? '',
    surfaceId: session?.meta?.surfaceId ?? '',
    surfaceName: session?.meta?.surface ?? '',
  });

  // Derived values up front (must come before any early-return so hook order
  // is stable even when session is missing).
  const activity = recoveryActivities.find((a) => a.id === session?.meta?.recoveryId);
  const hasModes = !!(activity && activity.modes);

  // Available surfaces for the currently-selected mode in the edit draft.
  // Filtered by user equipment, same pattern as Endurance page.
  const editSurfaces = useMemo(() => {
    if (!hasModes || !draft.mode || !activity.modes[draft.mode]) return [];
    return (activity.modes[draft.mode].surfaces || []).filter((s) => {
      if (!s.requiresEquipment || s.requiresEquipment.length === 0) return true;
      if (profile.fullGym) return true;
      return s.requiresEquipment.every((eq) => (profile.equipment || []).includes(eq));
    });
  }, [hasModes, activity, draft.mode, profile.equipment, profile.fullGym]);

  if (!session) {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center py-24">
          <AlertCircle className="w-8 h-8 text-enf-role-warning mx-auto mb-3" />
          <h1 className="font-display text-4xl tracking-wide leading-none mb-2">Session not found.</h1>
          <p className="text-enf-text-secondary mb-6">We couldn't find that session in your history.</p>
          <Link to="/endurance" className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Endurance
          </Link>
        </div>
      </div>
    );
  }

  const isDistance = activity && DISTANCE_ACTIVITIES.has(activity.id);
  const platform = session.meta?.platform || 'manual';
  const isManual = platform === 'manual';

  const Ico = activity?.id === 'cycling' ? Bike : (activity?.id === 'hiking' || activity?.id === 'rock-climbing') ? Mountain : Footprints;

  const saveEdits = () => {
    // Mutate history entry in-place via the profile context (history is part of profile state pattern)
    // We need to find the session and update its fields. Using the same pattern the rest of app uses
    // by writing the patch back to localStorage via the update fn on a special key.
    // Simpler: directly mutate localStorage. ProfileContext exposes `update` for profile only, not history.
    // For now: persist edits to localStorage manually since backend isn't live yet.
    const KEY = 'enferno_history_v1';
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      // Compute updated mode/surface if the activity supports modes
      const surfaceObj = hasModes
        ? (editSurfaces.find((s) => s.id === draft.surfaceId) || null)
        : null;
      const updated = raw.map((h) =>
        h.id === session.id
          ? {
              ...h,
              duration: Number(draft.duration) || h.duration,
              // Distance is stored in canonical MILES regardless of units pref.
              distance: draft.distance === '' ? null : +parseDistanceInput(draft.distance, units).toFixed(3),
              meta: {
                ...h.meta,
                notes: draft.notes,
                heartRateAvg: draft.heartRateAvg === '' ? null : Number(draft.heartRateAvg),
                calories: draft.calories === '' ? null : Number(draft.calories),
                // Elevation is stored in canonical FEET regardless of units pref.
                elevation: draft.elevation === '' ? null : (
                  units === 'metric' ? +mToFt(Number(draft.elevation)).toFixed(0) : Number(draft.elevation)
                ),
                ...(hasModes
                  ? {
                      mode: draft.mode || h.meta?.mode || 'standard',
                      surface: surfaceObj?.name || (draft.surfaceId === '' ? null : h.meta?.surface) || null,
                      surfaceId: surfaceObj?.id || (draft.surfaceId === '' ? null : h.meta?.surfaceId) || null,
                    }
                  : {}),
              },
            }
          : h
      );
      localStorage.setItem(KEY, JSON.stringify(updated));
      window.location.reload(); // simplest way to re-hydrate from localStorage given the current context pattern
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = () => {
    removeWorkout(session.id);
    navigate('/progress');
  };

  const fmtDate = new Date(session.date).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  const fmtTime = new Date(session.date).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        <Link to="/endurance" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Endurance
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2 flex items-center gap-2">
              <Ico className="w-3.5 h-3.5" /> {activity?.name || session.title.split(' · ')[0]}
              {session.meta?.surface && <span className="text-enf-text-muted">· {session.meta.surface}</span>}
            </div>
            <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-none mb-3">{session.title}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-enf-text-secondary">
              <span>{fmtDate}</span>
              <span className="text-enf-text-muted">·</span>
              <span>{fmtTime}</span>
              <span className="text-enf-text-muted">·</span>
              <span className="capitalize text-enf-role-info">{platform === 'manual' ? 'Manual entry' : platform}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm flex items-center gap-2 transition-colors">
                <Edit3 className="w-4 h-4" /> Edit
              </button>
            ) : (
              <>
                <button onClick={() => setEditing(false)} className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm flex items-center gap-2 transition-colors">
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button onClick={saveEdits} className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center gap-2 transition-colors">
                  <Save className="w-4 h-4" /> Save
                </button>
              </>
            )}
          </div>
        </div>

        {/* Cloud sync vs manual entry toggle banner */}
        <div className="mb-6 p-4 bg-enf-surface-deep border border-enf-surface-border rounded-xl flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isManual ? <NotebookPen className="w-4 h-4 text-enf-role-info" /> : <Cloud className="w-4 h-4 text-enf-role-info" />}
              <span className="text-xs uppercase tracking-widest font-semibold text-enf-role-info">
                {isManual ? 'Logged manually' : `Imported from ${platform}`}
              </span>
            </div>
            <p className="text-sm text-enf-text-secondary">
              {isManual
                ? 'This session was entered by hand. Edit any field to refine your record.'
                : `Numbers pulled from ${platform}. Device sync hookup coming with cloud backend — until then, edits stay local.`}
            </p>
          </div>
          <button
            disabled
            className="px-4 py-2 rounded-md text-sm font-semibold bg-enf-surface-mid border border-enf-surface-border text-enf-text-muted cursor-not-allowed flex items-center gap-2"
            title="Cloud device sync ships with the backend"
          >
            <Cloud className="w-4 h-4" /> Connect device · soon
          </button>
        </div>

        {/* Stats grid */}
        {isDistance ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat icon={Clock} label="Time" value={editing ? null : (session.duration || null)} unit="min" />
            <Stat icon={MapPin} label="Distance" value={editing ? null : (session.distance != null ? displayDistance(session.distance, units) : null)} unit={distanceUnit(units)} />
            <Stat icon={TrendingUp} label="Elevation" value={editing ? null : (session.meta?.elevation != null ? displayElevation(session.meta.elevation, units) : null)} unit={elevationUnit(units)} />
            <Stat icon={Heart} label="Avg HR" value={editing ? null : (session.meta?.heartRateAvg || null)} unit="bpm" />
            <Stat icon={Flame} label="Calories" value={editing ? null : (session.meta?.calories || null)} unit="kcal" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <Stat icon={Clock} label="Time" value={editing ? null : (session.duration || null)} unit="min" />
            <Stat icon={Heart} label="Avg HR" value={editing ? null : (session.meta?.heartRateAvg || null)} unit="bpm" />
            <Stat icon={Flame} label="Calories" value={editing ? null : (session.meta?.calories || null)} unit="kcal" />
          </div>
        )}

        {/* Edit fields */}
        {editing && (
          <div className="mb-6 p-5 bg-enf-surface-deep border border-enf-border-special/40 rounded-xl">
            <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-4">Edit session</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Duration (min)" value={draft.duration} onChange={(v) => setDraft((d) => ({ ...d, duration: v }))} type="number" />
              {isDistance && (
                <Field label={`Distance (${distanceUnit(units)})`} value={draft.distance} onChange={(v) => setDraft((d) => ({ ...d, distance: v }))} type="number" step="0.01" />
              )}
              {isDistance && (
                <Field label={`Elevation gain (${elevationUnit(units)})`} value={draft.elevation} onChange={(v) => setDraft((d) => ({ ...d, elevation: v }))} type="number" />
              )}
              <Field label="Avg heart rate (bpm)" value={draft.heartRateAvg} onChange={(v) => setDraft((d) => ({ ...d, heartRateAvg: v }))} type="number" />
              <Field label="Calories (kcal)" value={draft.calories} onChange={(v) => setDraft((d) => ({ ...d, calories: v }))} type="number" />
            </div>

            {/* Mode + Surface — only for activities that support them. */}
            {hasModes && (
              <div className="mt-4 pt-4 border-t border-enf-surface-border">
                <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-3">Mode &amp; surface</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-enf-text-muted font-semibold mb-1.5 block">Mode</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(activity.modes).map(([mId, mObj]) => (
                        <button
                          key={mId}
                          type="button"
                          onClick={() => setDraft((d) => ({ ...d, mode: mId, surfaceId: '' }))}
                          className={`px-2 py-1.5 text-xs rounded-md border capitalize transition-colors ${
                            draft.mode === mId ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                          }`}
                          data-testid={`edit-mode-${mId}`}
                        >
                          {mObj.label || mId}
                        </button>
                      ))}
                    </div>
                  </div>
                  {editSurfaces.length > 0 && (
                    <div>
                      <label className="text-xs text-enf-text-muted font-semibold mb-1.5 block">Surface / location</label>
                      <div className="grid grid-cols-2 gap-2">
                        {editSurfaces.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setDraft((d) => ({ ...d, surfaceId: s.id, surfaceName: s.name }))}
                            className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${
                              draft.surfaceId === s.id ? 'bg-enf-role-info/20 border-enf-role-info text-enf-role-info' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                            }`}
                            data-testid={`edit-surface-${s.id}`}
                          >
                            {s.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delete this session — two-tap confirm to prevent accidents */}
            <div className="mt-4 pt-4 border-t border-enf-surface-border">
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="text-xs text-enf-role-warning/80 hover:text-enf-role-warning flex items-center gap-1.5 transition-colors"
                  data-testid="activity-delete-button"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete this session
                </button>
              ) : (
                <div className="flex items-center justify-between gap-3 p-3 bg-enf-role-warning/10 border border-enf-role-warning/40 rounded-md">
                  <span className="text-xs text-enf-role-warning">Delete this session? This can't be undone.</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold" data-testid="activity-delete-cancel">
                      Cancel
                    </button>
                    <button type="button" onClick={handleDelete} className="px-3 py-1.5 bg-enf-role-warning hover:bg-enf-role-warning/80 rounded text-xs font-semibold text-enf-text-primary" data-testid="activity-delete-confirm">
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <NotebookPen className="w-4 h-4 text-enf-accent-fg" />
            <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Notes</div>
          </div>
          {editing ? (
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="How did it feel? What worked? What didn't?"
              rows={4}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none"
            />
          ) : session.meta?.notes ? (
            <p className="text-sm text-enf-text-secondary leading-relaxed whitespace-pre-wrap">{session.meta.notes}</p>
          ) : (
            <p className="text-sm text-enf-text-muted italic">No notes yet. Tap Edit to add some.</p>
          )}
        </div>

        {/* Route map & splits — GPS-tracked sessions get a real polyline + per-unit splits. */}
        {isDistance && session.meta?.gps ? (
          <GpsRoutePreview gps={session.meta.gps} units={session.meta?.units || session.meta.gps.units || 'imperial'} />
        ) : isDistance && (
          <div className="mt-5 p-6 bg-enf-surface-deep border border-enf-surface-border rounded-xl text-center">
            <ActivityIcon className="w-6 h-6 text-enf-text-muted mx-auto mb-2" />
            <div className="font-display text-xl tracking-wide leading-none mb-2">Route map &amp; mile splits</div>
            <p className="text-sm text-enf-text-muted">
              Tap <span className="text-enf-role-info">Track outdoors with GPS</span> on the Endurance page to record your route — the polyline preview and splits will land here. Wearable / Strava / Garmin sync ships in a follow-up pass.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- GPS route + splits ----------

const fmtPaceSmall = (sec) => {
  if (!sec || !isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const GpsRoutePreview = ({ gps, units }) => {
  const poly = Array.isArray(gps.polyline) ? gps.polyline : [];
  const splits = gps.stats?.splits || [];
  const unitsLabel = units === 'metric' ? 'km' : 'mi';

  // Start coordinate for "View on Google Maps" — opens Google Maps centered
  // on the activity's start point. The polyline itself can't be rendered
  // accurately on Google Maps without a paid Directions/Static Maps API call
  // (and Maps Directions would route along streets, which is wrong for a
  // trail run / off-road ride). Linking to the start gives users free map
  // context for "where was this?" while the SVG above shows the actual shape.
  const startCoord = poly.length > 0 ? poly[0] : null;
  const googleMapsUrl = startCoord
    ? `https://www.google.com/maps?q=${startCoord[0]},${startCoord[1]}`
    : null;

  // Fit polyline into a 600×300 viewBox with 12px padding. Flip lat (y) so north is up.
  let pathD = '';
  if (poly.length >= 2) {
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const [la, lo] of poly) {
      if (la < minLat) minLat = la;
      if (la > maxLat) maxLat = la;
      if (lo < minLng) minLng = lo;
      if (lo > maxLng) maxLng = lo;
    }
    const w = 600, h = 300, pad = 12;
    const latRange = Math.max(1e-9, maxLat - minLat);
    const lngRange = Math.max(1e-9, maxLng - minLng);
    // Preserve aspect: pick the scale that fits both axes
    const sx = (w - 2 * pad) / lngRange;
    const sy = (h - 2 * pad) / latRange;
    const s = Math.min(sx, sy);
    const offX = pad + ((w - 2 * pad) - lngRange * s) / 2;
    const offY = pad + ((h - 2 * pad) - latRange * s) / 2;
    const pts = poly.map(([la, lo]) => {
      const x = offX + (lo - minLng) * s;
      const y = offY + (maxLat - la) * s; // flip Y so north = up
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    pathD = `M ${pts.join(' L ')}`;
  }

  return (
    <div className="mt-5 space-y-4">
      <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-enf-surface-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-enf-accent-fg" />
            <span className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Route preview</span>
          </div>
          <span className="text-[10px] text-enf-text-muted">{poly.length} anchor points · {gps.sampleCount || 0} raw samples</span>
        </div>
        <div className="bg-enf-surface-mid">
          {pathD ? (
            <svg viewBox="0 0 600 300" className="w-full block" preserveAspectRatio="xMidYMid meet">
              <rect x="0" y="0" width="600" height="300" fill="#000000" />
              <path d={pathD} fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {/* Start + end markers */}
              <circle cx={pathD.split(' ')[1].split(',')[0]} cy={pathD.split(' ')[1].split(',')[1]} r="5" fill="#22c55e" />
              {(() => {
                const tokens = pathD.split(' ');
                const last = tokens[tokens.length - 1];
                const [x, y] = last.split(',');
                return <circle cx={x} cy={y} r="5" fill="#dc2626" />;
              })()}
            </svg>
          ) : (
            <div className="py-12 text-center text-enf-text-muted text-sm">No route polyline available.</div>
          )}
        </div>
        {googleMapsUrl && (
          <div className="px-5 py-3 border-t border-enf-surface-border flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-enf-text-muted">Want streets, terrain, satellite?</span>
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-enf-role-info hover:text-enf-text-primary flex items-center gap-1.5 transition-colors"
              data-testid="gps-view-google-maps"
              title="Opens the activity's start point on Google Maps in a new tab"
            >
              <ExternalLink className="w-3 h-3" /> View start on Google Maps
            </a>
          </div>
        )}
      </div>

      {splits.length > 0 && (
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-enf-surface-border flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-enf-accent-fg" />
            <span className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Splits ({unitsLabel})</span>
          </div>
          <div className="divide-y divide-zinc-900">
            {splits.map((sp) => (
              <div key={sp.n} className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-enf-text-secondary font-semibold">{unitsLabel.toUpperCase()} {sp.n}</span>
                <span className="text-enf-text-secondary font-mono">{fmtPaceSmall(sp.paceSecPerUnit)}</span>
                {sp.gainM > 0 && <span className="text-xs text-enf-text-muted">+{sp.gainM}m</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', step }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">{label}</label>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => e.target.select()}
      className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
    />
  </div>
);

export default ActivityDetail;
